import { _lcs } from "../i18n";

/**
 * The guest-app as an installable app: Service Worker, updates, installation.
 *
 * Service Worker (pwa/sw.js, built by scripts/postbuild.mjs)
 *   Registered in release builds only. The dev server serves index.html with
 *   its literal "?INFRONTCACHEBREAK" and has no sw.js — a worker there would
 *   only cache yesterday's code — so in development any leftover registration
 *   is removed instead.
 *
 * Updates
 *   A new deploy installs in the background and waits. The guest sees
 *   "Neue Version verfügbar" and decides: only a tap on "Aktualisieren" lets
 *   the new worker take over, and only that tap's controllerchange reloads —
 *   once. The first install (no previous worker) also fires controllerchange
 *   through clients.claim(); without the tap it reloads nothing, so there is
 *   no loop. Checked on start and whenever the app comes back to the
 *   foreground, at most every UPDATE_CHECK_MS.
 *
 * Installation
 *   Android/Chromium hands us `beforeinstallprompt`: kept, and fired from
 *   "App installieren" in the menu or the one-time hint; `appinstalled` then
 *   confirms with a toast. iOS has no such event and no API — only "Teilen →
 *   Zum Home-Bildschirm" — so it is sent to the install page's step-by-step
 *   instructions instead. Nothing is offered when the app already runs
 *   installed. The hint is a banner on the home screen only: from the second
 *   visit, at most once per visit and on at most HINT_MAX_VISITS visits, gone
 *   for good once dismissed or installed. "App installieren" in the menu
 *   stays available regardless.
 *
 * Changes are announced as a "pwa-change" event on the app, so the menu can
 * show or hide its install item without asking.
 */
export class Pwa
{
    static UPDATE_CHECK_MS = 30 * 60 * 1000;
    // The install hint waits for a returning guest: on the very first open the
    // guest has not seen the app yet.
    static HINT_FROM_VISIT = 2;
    static HINT_MAX_VISITS = 3;

    constructor( app )
    {
        this.app = app;
        this.registration = null;
        this._installPrompt = null;
        this._updateBanner = null;
        this._hintBanner = null;
        this._updateAccepted = false;
        this._reloading = false;
        this._lastUpdateCheck = 0;
        this._started = false;
        this._hintShownThisVisit = false;
        this._installed = false;
    }

    /* --------------------------------- Detection -------------------------------- */

    static isStandalone()
    {
        const mq = ( q ) => !!( window.matchMedia && window.matchMedia( q ).matches );
        return mq( "(display-mode: standalone)" ) || mq( "(display-mode: fullscreen)" ) ||
            mq( "(display-mode: minimal-ui)" ) || window.navigator.standalone === true;
    }

    static isIos()
    {
        const ua = navigator.userAgent || "";
        // iPadOS reports itself as a Mac; touch gives it away.
        return /iPad|iPhone|iPod/.test( ua ) || ( /Macintosh/.test( ua ) && navigator.maxTouchPoints > 1 );
    }

    /** The dev server leaves frontfire's cache-break placeholder in index.html. */
    static isDevBuild()
    {
        const script = document.querySelector( 'script[src*="app/main.js"]' );
        return !script || /INFRONTCACHEBREAK/.test( script.getAttribute( "src" ) || "" );
    }

    /* ---------------------------------- Start ---------------------------------- */

    start()
    {
        if ( this._started )
        {
            return;
        }
        this._started = true;

        document.documentElement.classList.toggle( "is-standalone", Pwa.isStandalone() );

        window.addEventListener( "beforeinstallprompt", ( e ) =>
        {
            // Keep it for our own button instead of the browser's mini-infobar.
            e.preventDefault();
            this._installPrompt = e;
            this._changed();
        } );

        // Fires however the guest installed — our button, the hint or the
        // browser's own menu — so the receipt lives here rather than in install().
        window.addEventListener( "appinstalled", () =>
        {
            this._installPrompt = null;
            this._installed = true;
            this._removeHint();
            this._changed();
            this.app.view.toast( _lcs( "Die App ist installiert. Du findest sie jetzt auf deinem Home-Bildschirm." ), "success", 5000 );
        } );

        this._countVisit();
        this._registerServiceWorker();
    }

    /* -------------------------------- Installation ------------------------------ */

    /**
     * Running as the installed app, or installed from this very tab. The
     * latter matters on Chromium: after installing, the browser tab stays open
     * — still not standalone, and with no prompt left to tell us anything.
     *
     * An app installed in an EARLIER visit is recognised by its absence:
     * Chromium only fires `beforeinstallprompt` while the app is NOT installed,
     * so no prompt means no install offer.
     */
    isInstalled()
    {
        return Pwa.isStandalone() || this._installed === true;
    }

    /** "prompt" (Chromium), "ios" (instructions) or null (installed / not possible). */
    installMode()
    {
        if ( this.isInstalled() )
        {
            return null;
        }
        if ( this._installPrompt )
        {
            return "prompt";
        }
        return Pwa.isIos() ? "ios" : null;
    }

    canInstall()
    {
        return this.installMode() !== null;
    }

    async install()
    {
        const mode = this.installMode();
        this._removeHint();

        if ( mode === "prompt" )
        {
            const prompt = this._installPrompt;
            // A prompt can be used once; Chromium fires a new one if the guest declines.
            this._installPrompt = null;
            try
            {
                await prompt.prompt();
                await prompt.userChoice;
            }
            catch ( e )
            {
                console.error( e );
            }
            this._changed();
            return;
        }

        // Three taps through the browser's own menus, which a guest does not
        // find from one sentence in a dialog — so a page that walks them
        // through it (states/install/InstallState).
        if ( mode === "ios" )
        {
            this.app.router.redirect( "/install" );
        }
    }

    /**
     * The install hint. Called by the home screen when it enters; shows
     * nothing when the app cannot be installed, the guest dismissed it, this
     * is the first visit, it was already shown this visit, or on enough visits.
     */
    maybeShowInstallHint()
    {
        if ( this._hintBanner || this._hintShownThisVisit || !this.canInstall() )
        {
            return;
        }
        const storage = this.app.storage;
        const shows = +storage.load( "pwaInstallHintShows" ) || 0;
        if ( storage.load( "pwaInstallHintDismissed" ) === true || shows >= Pwa.HINT_MAX_VISITS ||
             ( +storage.load( "pwaVisits" ) || 0 ) < Pwa.HINT_FROM_VISIT )
        {
            return;
        }
        this._hintShownThisVisit = true;
        storage.save( "pwaInstallHintShows", shows + 1 );

        this._hintBanner = this.app.view.banner( {
            icon : "monitor-smartphone",
            // Short on purpose: it shares a line with its button on a phone.
            text : _lcs( "Als App auf den Home-Bildschirm — auch offline nutzbar." ),
            actionLabel : _lcs( "Installieren" ),
            onAction : () => this.install(),
            onDismiss : () =>
            {
                storage.save( "pwaInstallHintDismissed", true );
                this._hintBanner = null;
            }
        } );
    }

    /** The home screen is left: the hint goes with it (it may come back next visit). */
    hideInstallHint()
    {
        if ( this._hintBanner )
        {
            this._hintBanner.remove();
            this._hintBanner = null;
        }
    }

    /** Installing (or trying to) answers the hint for good. */
    _removeHint()
    {
        this.hideInstallHint();
        this.app.storage.save( "pwaInstallHintDismissed", true );
    }

    _countVisit()
    {
        try
        {
            this.app.storage.save( "pwaVisits", ( +this.app.storage.load( "pwaVisits" ) || 0 ) + 1 );
        }
        catch ( e )
        {
            // Private mode without storage: no hint, nothing else lost.
        }
    }

    /* ------------------------------ Service Worker ----------------------------- */

    async _registerServiceWorker()
    {
        if ( !( "serviceWorker" in navigator ) )
        {
            return;
        }

        if ( Pwa.isDevBuild() )
        {
            // A worker left over from testing a release build on this origin
            // would keep serving its cached shell over the dev server.
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all( registrations.map( ( r ) => r.unregister() ) );
            return;
        }

        navigator.serviceWorker.addEventListener( "controllerchange", () =>
        {
            if ( !this._updateAccepted || this._reloading )
            {
                return;
            }
            this._reloading = true;
            window.location.reload();
        } );

        try
        {
            // The API base goes along so the worker knows which reads are the
            // house's public content (see sw.js CONTENT_ROUTES).
            const url = "sw.js?api=" + encodeURIComponent( this.app.config.get( "api" ) );
            this.registration = await navigator.serviceWorker.register( url, { scope : "./", updateViaCache : "none" } );
        }
        catch ( e )
        {
            console.error( "Service Worker registration failed:", e );
            return;
        }

        const reg = this.registration;
        if ( reg.waiting && navigator.serviceWorker.controller )
        {
            this._handleWaiting( reg.waiting );
        }
        reg.addEventListener( "updatefound", () =>
        {
            const worker = reg.installing;
            if ( !worker )
            {
                return;
            }
            worker.addEventListener( "statechange", () =>
            {
                // "installed" with a controller = an update, not the first install.
                if ( worker.state === "installed" && navigator.serviceWorker.controller )
                {
                    this._handleWaiting( worker );
                }
            } );
        } );

        this._lastUpdateCheck = Date.now();
    }

    /** Called when the app returns to the foreground (see App lifecycle). */
    checkForUpdate()
    {
        if ( !this.registration || Date.now() - this._lastUpdateCheck < Pwa.UPDATE_CHECK_MS )
        {
            return;
        }
        this._lastUpdateCheck = Date.now();
        this.registration.update().catch( () => {} );
    }

    /**
     * A new worker is installed and waiting. Navigations are network first, so
     * the page itself is usually ALREADY the new build while the old worker is
     * still in charge — and the old worker is what receives push messages,
     * shows (or fails to show) them and answers fetches. When the waiting
     * worker is exactly the build this page runs, there is nothing to ask the
     * guest: it takes over at once, without a reload. Only a worker newer than
     * the page gets the "Neue Version" banner.
     */
    async _handleWaiting( worker )
    {
        const [ waitingVersion, pageVersion ] = [ await Pwa._workerVersion( worker ), Pwa.pageVersion() ];
        if ( waitingVersion && pageVersion && waitingVersion === pageVersion )
        {
            worker.postMessage( { type : "SKIP_WAITING" } );
            return;
        }
        this._offerUpdate( worker );
    }

    /** This page's build stamp — frontfire's cache-break on app/main.js (see scripts/postbuild.mjs). */
    static pageVersion()
    {
        const script = document.querySelector( 'script[src*="app/main.js"]' );
        const match = /main\.js\?(\d+)/.exec( ( script && script.getAttribute( "src" ) ) || "" );
        return match ? match[ 1 ] : null;
    }

    /** The build stamp of a worker (sw.js answers GET_VERSION), or null. */
    static _workerVersion( worker )
    {
        return new Promise( ( resolve ) =>
        {
            const channel = new MessageChannel();
            const timer = setTimeout( () => resolve( null ), 2000 );
            channel.port1.onmessage = ( e ) =>
            {
                clearTimeout( timer );
                resolve( e.data ? String( e.data ) : null );
            };
            try
            {
                worker.postMessage( { type : "GET_VERSION" }, [ channel.port2 ] );
            }
            catch ( e )
            {
                clearTimeout( timer );
                resolve( null );
            }
        } );
    }

    _offerUpdate( worker )
    {
        if ( this._updateBanner )
        {
            return;
        }
        this._updateBanner = this.app.view.banner( {
            icon : "rotate-cw",
            text : _lcs( "Neue Version der Gäste-App verfügbar." ),
            actionLabel : _lcs( "Aktualisieren" ),
            onAction : () =>
            {
                this._updateAccepted = true;
                worker.postMessage( { type : "SKIP_WAITING" } );
            },
            // Dismissed = later: the waiting version takes over by itself once
            // every tab of the app is closed.
            onDismiss : () => { this._updateBanner = null; }
        } );
    }

    _changed()
    {
        this.app.dispatchEvent( new CustomEvent( "pwa-change" ) );
    }
}
